import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useAnimationFrame, useMotionValue } from "framer-motion";
import { ArrowLeft, ArrowRight, Pause, Play, ArrowUpRight } from "lucide-react";
import { SectionHead } from "@/components/layout/Motion";
import { AIRLINES, BRAND } from "@/lib/site";

const CARD = 172;

export const AirlineCarousel = ({ list = AIRLINES, title = "Flights from Leading Airlines", text = "Around the corner. Across the world." }) => {
  const x = useMotionValue(0);
  const [paused, setPaused] = useState(false);
  const hover = useRef(false);
  const width = list.length * CARD;

  useAnimationFrame((_, delta) => {
    if (paused || hover.current) return;
    let next = x.get() - delta * 0.04;
    if (next <= -width) next += width;
    x.set(next);
  });

  const nudge = (dir) => {
    let next = x.get() + dir * CARD * 2;
    if (next <= -width) next += width;
    if (next > 0) next -= width;
    x.set(next);
  };

  return (
    <section className="bg-white py-12" data-testid="airline-carousel">
      <div className="mx-auto max-w-[1320px] px-4 sm:px-8">
        <SectionHead title={title} text={text}
          action={<Link to="/deals" data-testid="explore-airline-deals" className="group flex items-center gap-1.5 text-sm font-bold text-brand">Explore airline deals <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" /></Link>} />
        <div className="mt-7 overflow-hidden" onMouseEnter={() => { hover.current = true; }} onMouseLeave={() => { hover.current = false; }}>
          <motion.ul style={{ x }} className="flex w-max">
            {[...list, ...list].map((a, i) => (
              <li key={`${a.code}-${i}`} className="pr-3" aria-hidden={i >= list.length}>
                <Link to={`/airlines/${a.slug}`} title={a.name} data-testid={i < list.length ? `airline-card-${a.code}` : undefined}
                  className="grid h-24 w-40 place-items-center rounded-xl border border-slate-200 bg-white p-4 transition-[border-color,box-shadow] duration-300 hover:border-brand/40 hover:shadow-lg">
                  <img src={a.logo} alt={`${a.name} logo`} loading="lazy" className="max-h-12 w-auto object-contain" />
                </Link>
              </li>
            ))}
          </motion.ul>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          {[[ArrowLeft, () => nudge(1), "prev"], [paused ? Play : Pause, () => setPaused((p) => !p), "pause"], [ArrowRight, () => nudge(-1), "next"]].map(([Icon, fn, k]) => (
            <button key={k} onClick={fn} aria-label={k} data-testid={`airline-carousel-${k}`} className="grid h-9 w-9 place-items-center rounded-full border border-slate-200 text-ink transition-colors hover:border-brand hover:text-brand">
              <Icon className="h-4 w-4" />
            </button>
          ))}
        </div>
        <p className="mt-6 text-xs text-slate-500">Independent travel assistance by {BRAND}. Airline names and logos are shown for reference only; trademarks belong to their respective owners.</p>
      </div>
    </section>
  );
};
